import React from 'react';
import { Box, Typography } from '@mui/material';
import { ResponsiveContainer, AreaChart as RechartsAreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import ChartHeader from './ChartHeader';
import { ChartHeaderProps } from './types';
import ChartCardWrapper from './ChartCardWrapper';
import ChartLegend from './ChartLegend';
import { fontSizes } from '../../theme/theme';

interface AreaChartData {
  [key: string]: string | number;
}

interface StackedAreaChartProps extends ChartHeaderProps {
  data: AreaChartData[];
  dataKeys: string[];
  xAxisKey: string;
  colors?: string[];
  height?: number;
  graphHeight?: number;
  xAxisTitle?: string;
  yAxisTitle?: string;
  onDetailsClick?: (data?: any) => void;
}

const StackedAreaChart: React.FC<StackedAreaChartProps> = ({
  title,
  description,
  data,
  dataKeys,
  xAxisKey,
  colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'],
  height = 300,
  graphHeight = 250,
  xAxisTitle,
  yAxisTitle,
  details,
  onDetailsClick
}) => {

  const handlePointClick = (data: any, dataKey: string) => {
    if (onDetailsClick) {
      onDetailsClick({ dataKey, dataRow: data });
    }
    console.log('Clicked Series Key:', dataKey);
    console.log('Clicked Data Row:', data.payload);
    console.log('Clicked Value:', data.value);
  };

  return (
    <ChartCardWrapper sx={{ height }}>
      <ChartHeader title={title} description={description} details={details} />
      <ResponsiveContainer width="100%" minHeight={graphHeight}>
        <RechartsAreaChart
          data={data}
          margin={{ top: 20, right: 10, left: 5, bottom: 20 }}
        >
          <defs>
            {dataKeys.map((key, index) => (
              <linearGradient key={`gradient-${key}`} id={`gradient-${key}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={colors[index % colors.length]} stopOpacity={0.36} />
                <stop offset="95%" stopColor={colors[index % colors.length]} stopOpacity={0.08} />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
          <XAxis
            dataKey={xAxisKey}
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: fontSizes.graph.axis, fill: '#6B7280' }}
            label={xAxisTitle ? { value: xAxisTitle, position: 'insideBottom', offset: -10, style: { textAnchor: 'middle', fontSize: fontSizes.graph.axis, fill: '#374151' } } : undefined}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: fontSizes.graph.axis, fill: '#6B7280' }}
            label={yAxisTitle ? { value: yAxisTitle, angle: -90, position: 'insideLeft', offset: 20, style: { textAnchor: 'middle', fontSize: fontSizes.graph.axis, fill: '#374151' } } : undefined}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: '#F9FAFB',
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              fontSize: `${fontSizes.graph.tooltip}px`
            }}
            cursor={{ stroke: 'rgba(59, 130, 246, 0.2)', strokeWidth: 1 }}
          />
          {dataKeys.map((key, index) => (
            <Area
              key={key}
              type="monotone"
              dataKey={key}
              stroke={colors[index % colors.length]}
              fill={`url(#gradient-${key})`}
              strokeWidth={2}
              dot={{
                fill: colors[index % colors.length],
                strokeWidth: 2,
                r: 4,
                onClick: (e: any, payload: any) => handlePointClick(payload, key),
                cursor: 'pointer'
              }}
              activeDot={{
                r: 6,
                strokeWidth: 2,
                stroke: colors[index % colors.length],
                onClick: (e: any, payload: any) => handlePointClick(payload, key),
                cursor: 'pointer'
              }}
              stackId="1"
            />
          ))}
        </RechartsAreaChart>
      </ResponsiveContainer>
      <ChartLegend 
        bottomDescription={details?.bottomDescription}
        subPoints={details?.subPoints}
        colors={colors}
      />
    </ChartCardWrapper>
  );
};

export default StackedAreaChart;
